import { ControlSection, ToggleControlItem } from '@bloomfieldrobotics/canopy';
import { useCallback, useEffect, useState } from 'react';
import { useRanchesByCustomer } from '../../../services/entities/hooks/useRanchesByCustomer';
import { Ranch } from '../../../services/entities/impl/blfApi/schemas/ranch';

type Props = {
  customerId: string;
  onRanchesLoad?: (ranches: Ranch[] | undefined) => void;
  onRanchToggle: (
    activeRanchIds: Ranch['id'][],
    allToggleStates?: Map<string, boolean>
  ) => void;
};

/**
 * TODO: Re-write this with hidden block logic. Basically/Hopefully a
 * copy/paste of CollectionBlocksControlSection and then reversing the
 * logic of anywhere this component has been used.
 */
export const RanchToggleControlSection = ({
  customerId,
  onRanchToggle,
  onRanchesLoad,
}: Props) => {
  // Get all the Ranches for the Customer
  const { ranches, isRanchesLoading, isSuccess, isError, isUninitialized } =
    useRanchesByCustomer(customerId);

  useEffect(() => {
    if (!isRanchesLoading && !isUninitialized && (isSuccess || isError)) {
      onRanchesLoad?.(ranches);
    }
  }, [
    isRanchesLoading,
    ranches,
    onRanchesLoad,
    isError,
    isSuccess,
    isUninitialized,
  ]);

  // TODO: Utilize useReducer instead
  // Ranch multi-toggle state
  const [ranchesSelected, setRanchesSelected] = useState<Map<string, boolean>>(
    new Map()
  );

  useEffect(() => {
    if (ranches && isSuccess) {
      const newRanchesSelected = new Map<string, boolean>();
      ranches.forEach((ranch) => {
        newRanchesSelected.set(ranch.id, true);
      });
      setRanchesSelected(newRanchesSelected);
    }
  }, [ranches, isSuccess]);

  const handleToggleItemClick = useCallback((ranch: Ranch) => {
    setRanchesSelected((prev) => {
      const checked = prev.get(ranch.id);
      return new Map(prev).set(ranch.id, !checked);
    });
  }, []);

  /**
   * When the internal state of the active ranches changes, trigger onRanchToggle.
   * I am doing it this way, rather than in the handleToggleItemClick, because
   * this component loads the ranches as well, so we need to fire off the onRanchToggle
   * for our initial all selected state.
   */
  useEffect(() => {
    const selectedRanchIds: string[] = [];
    for (const [id, isSelected] of ranchesSelected.entries()) {
      if (isSelected) {
        selectedRanchIds.push(id);
      }
    }

    onRanchToggle(selectedRanchIds, ranchesSelected);
  }, [ranchesSelected, onRanchToggle]);

  return (
    <ControlSection title='Ranch' loading={isRanchesLoading}>
      {ranches?.map((ranch) => (
        <ToggleControlItem
          key={ranch.id}
          label={ranch.name}
          active={ranchesSelected.get(ranch.id)}
          onChange={() => {
            handleToggleItemClick(ranch);
          }}
        />
      ))}
    </ControlSection>
  );
};
